import React, {useState, useEffect, useRef} from 'react';
import {getWindow} from './../../Helpers/browsers';
import { useIntersect } from './../../Helpers/useIntersectionObserver';

export default function Ad({adUnit,adWidth,adHeight,sponsored=false,sizeMapping=null,targeting=null,clickUrl=null,loadDelay=2500}) {
  // don't even bother with ads in SSR
  if (typeof window == 'undefined') {
    return null;
  }

  const accountId = "15195428";
  const adRef = useRef();
  let theSlot = useRef();
  const intersectRef = useRef();

  const [adRendered, renderAd] = useState(false);
  const [slotId, updateSlotId] = useState('tpt-ad' + Math.floor(Math.random()*1000000));
  const [timer, setTimer] = useState();

  //Refreshable: targeting, sizeMapping, clickUrl, categoryExclusion, attributes, collapseEmptyDiv, companionAdService, forceSafeFrame, safeFrameConfig
  //Must Rebuild/Reload ad: adUnitPath, slotSize (width height), outOfPage, content, nonPersonalizedAds

  const [entry, observer] = useIntersect(intersectRef, {
    threshold: 1
  });

  function loadGPTScript() {
    return new Promise((resolve, reject) => {
      if (window.googletag) {
        resolve(window.googletag);
      }
      window.googletag = window.googletag || {};
      window.googletag.cmd = window.googletag.cmd || [];

      const scriptTag = document.createElement('script');
      scriptTag.src = 'https://www.googletagservices.com/tag/js/gpt.js';
      scriptTag.async = true;
      scriptTag.type = 'text/javascript';
      scriptTag.onerror = errs => {
        reject(errs);
      };
      scriptTag.onload = () => {
        resolve(window.googletag);
      };
      document.getElementsByTagName('head')[0].appendChild(scriptTag);
    });
  }

  function setTargets() {
    clearTargets();

    if (targeting) {
      Object.keys(JSON.parse(targeting)).forEach(key => {
        if (googletag && googletag.cmd && typeof googletag.cmd.push === 'function') {
          googletag.cmd.push(() => {
            theSlot.current.setTargeting(key, JSON.parse(targeting)[key]);
          });
        }
      });
    }
  }

  function clearTargets() {
    if (googletag && googletag.cmd && typeof googletag.cmd.push === 'function') {
      googletag.cmd.push(function () {
        theSlot.current.clearTargeting();
      });
    }
  }

  function refreshAd() {
    if (googletag && typeof googletag.pubads === 'undefined') {
      googletag.pubads().refresh([theSlot.current]);
    }
  }

  function buildAd() {
    if (googletag && googletag.cmd && typeof googletag.cmd.push === 'function') {
      googletag.cmd.push(function () {
        if (googletag && googletag.defineSlot && typeof googletag.defineSlot === 'function') {
          theSlot.current = googletag.defineSlot(`/${accountId}/${adUnit}`, [parseInt(adWidth), parseInt(adHeight)], slotId);
        }

        if (googletag && googletag.pubads && typeof googletag.pubads === 'function') {
          theSlot.current.addService(window.googletag.pubads());
        }

        setTargets();
        setClickUrl();
        setSizeMapping();
      });
    }
  }

  function displayAd() {
    if (googletag && googletag.display && typeof googletag.display === 'function') {
      googletag.display(theSlot.current);
    }
  }

  function setClickUrl() {
    if (clickUrl) {
      theSlot.current.setClickUrl(clickUrl);
    }
  }

  function setSizeMapping() {
    //Sample: [{viewport: [0, 0], slot: [320, 50]},{viewport: [768, 0], slot: [728, 90]}]
    const sizeMappingArray = sizeMapping
      .reduce((mapping, size) => {
        return mapping.addSize(size.viewport, size.slot);
      }, googletag.sizeMapping())
      .build();
    theSlot.current.defineSizeMapping(sizeMappingArray);
  }

  useEffect(() => {

    //load ad script if not already loaded
    getWindow().then(window => {

      loadGPTScript()
        .then(() => {
          //set page-level settings (page-level key-targeting goes here if necessary)
          //googletag.pubads().disableInitialLoad();
          if (googletag && typeof googletag.pubads === 'function' && typeof googletag.enableServices === 'function') {
            googletag.pubads().enableAsyncRendering();
            googletag.pubads().collapseEmptyDivs();
            googletag.enableServices();

            googletag.pubads().addEventListener('slotRenderEnded', (event) => {
              if (event.slot.getSlotElementId() === slotId) {
                renderAd(true);
              }
            });
          }
        });

        adRef.current.setAttribute('id',slotId);
        adRef.current.setAttribute('data-uuid',slotId);

        buildAd();

      })
      .catch(_ => {
        console.log('googletag failed to load');
      });

    return () => {
      if (googletag && googletag.destroySlots && typeof googletag.destroySlots === 'function') {
        googletag.destroySlots([theSlot.current]);
      }
    }
  }, []);

  useEffect(() => {
    if (theSlot.current) {
      if (entry.isIntersecting) {
        setTimer(setTimeout(checkStillInViewport, loadDelay, entry.isIntersecting, observer));
      } else {
        clearTimeout(timer);
      }
    }
  }, [entry]);

  function checkStillInViewport(viewing, observer) {
    if (viewing) {
      if (googletag && googletag.cmd && typeof googletag.cmd.push === 'function') {
        googletag.cmd.push(() => {
          displayAd();
        });
      }
      observer.disconnect();
    }
  }

  useEffect(() => {
    //Requires rebuilding the ad: adUnit, slotSize (width height), outOfPage, content, nonPersonalizedAds
    if (theSlot.current) {
      buildAd();
      //displayAd();
    }
  }, [adUnit, adWidth, adHeight]);

  useEffect(() => {
    //change ad targeting
    if (theSlot.current) {
      setTargets();
      refreshAd();
    }
  }, [targeting]);

  useEffect(() => {
    if (theSlot.current) {
      setClickUrl();
      refreshAd();
    }
  }, [clickUrl]);

  useEffect(() => {
    if (theSlot.current) {
      setSizeMapping();
      refreshAd();
    }
  }, [sizeMapping]);

  return (
    <div className="ad" ref={intersectRef} style={{padding: adRendered ? '10px' : '0'}}>
      {sponsored && adRendered && <div className={"sponsored"}>Sponsored</div>}
      <div ref={adRef} tabIndex="-1" className="ad-wrapper" />
    </div>
  )
}